import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Heading,
  UnorderedList,
  ListItem,
  OrderedList,
  Divider,
  useColorModeValue,
  VStack,
  HStack,
  Badge,
} from '@chakra-ui/react';

const ANNOUNCEMENT_STORAGE_KEY = 'announcement_2026_01_fee_policy_closed';

const AnnouncementModal = ({ isOpen, onClose }) => {
  const [hasSeenAnnouncement, setHasSeenAnnouncement] = useState(false);
  
  // 颜色模式
  const bgColor = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const headingColor = useColorModeValue('navy.700', 'white');
  const highlightColor = useColorModeValue('blue.600', 'blue.300');
  const secondaryHighlight = useColorModeValue('purple.600', 'purple.300');
  const redHighlight = useColorModeValue('red.500', 'red.300');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  // 检查是否已经查看过公告
  useEffect(() => {
    const hasClosed = localStorage.getItem(ANNOUNCEMENT_STORAGE_KEY);
    if (hasClosed) {
      setHasSeenAnnouncement(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(ANNOUNCEMENT_STORAGE_KEY, 'true');
    setHasSeenAnnouncement(true);
    onClose();
  };

  // 如果已经看过公告，不显示
  if (hasSeenAnnouncement && !isOpen) {
    return null;
  }

  return (
    <Modal 
      isOpen={isOpen && !hasSeenAnnouncement} 
      onClose={handleClose} 
      size="4xl" 
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent 
        bg={bgColor} 
        borderRadius="xl" 
        mx={{ base: '4', md: '8' }}
        maxH="90vh"
      >
        <ModalHeader 
          pb="2" 
          borderBottom="1px" 
          borderColor={borderColor}
        >
          <VStack align="start" spacing="2">
            <HStack>
              <Badge colorScheme="red" fontSize="sm" px="2" py="1" borderRadius="md">
                重要通知
              </Badge>
              <Badge colorScheme="green" fontSize="sm" px="2" py="1" borderRadius="md">
                费率优惠
              </Badge>
            </HStack>
            <Heading 
              as="h1" 
              size="lg" 
              color={headingColor}
              fontWeight="bold"
            >
              Moonbamboo支付系统关于新增两项费率优惠政策的通知
            </Heading>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody py="6" px={{ base: '4', md: '6' }}>
          <VStack align="stretch" spacing="6" fontSize="md" color={textColor}>
            {/* 开头问候 */}
            <Text>尊敬的Moonbamboo商户：</Text>
            
            <Text lineHeight="1.8">
              生效时间声明：自
              <Text as="span" color={highlightColor} fontWeight="bold">2026年1月1日</Text>
              起，Moonbamboo支付系统正式新增推出
              <Text as="span" color={highlightColor} fontWeight="bold">两项费率优惠政策</Text>
              ，原有抽佣模式仍可正常使用。为帮助您更好地了解政策内容，现将具体信息通知如下：
            </Text>

            <Divider />

            {/* 一、新增政策详情 */}
            <Box>
              <Heading as="h2" size="md" color={highlightColor} mb="4">
                一、新增政策详情
              </Heading>

              {/* 政策一 */}
              <Box mb="6" pl="4">
                <Heading as="h3" size="sm" color={headingColor} mb="3" fontWeight="bold">
                  政策一：平台托管优惠方案
                </Heading>
                <UnorderedList spacing="2" pl="4">
                  <ListItem>
                    <Text as="span" fontWeight="medium">综合费率：</Text>
                    仅收取交易金额的
                    <Text as="span" color={highlightColor} fontWeight="bold">0.6%</Text>
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="medium">提现时效：</Text>
                    <Text as="span" color={highlightColor} fontWeight="bold">T+1工作日</Text>
                    稳定到账
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="medium">资金管理：</Text>
                    交易资金由Moonbamboo平台提供金融级安全托管，支持账户明细查询与统一管理
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="medium">抽佣方式：</Text>
                    交易完成后
                    <Text as="span" color={redHighlight} fontWeight="bold">实时扣除手续费</Text>
                    ，无需提前充值备用金，资金零占用
                  </ListItem>
                </UnorderedList>
              </Box>

              {/* 政策二 */}
              <Box pl="4">
                <Heading as="h3" size="sm" color={headingColor} mb="3" fontWeight="bold">
                  政策二：微信/支付宝商户通直通方案
                </Heading>
                <UnorderedList spacing="2" pl="4">
                  <ListItem>
                    <Text as="span" fontWeight="medium">费率结构：</Text>
                    Moonbamboo收取
                    <Text as="span" color={secondaryHighlight} fontWeight="bold">0.4%</Text>
                    技术服务费，微信/支付宝商户通收取
                    <Text as="span" color={secondaryHighlight} fontWeight="bold">0.2%</Text>
                    通道费，综合费率总计
                    <Text as="span" color={highlightColor} fontWeight="bold">0.6%</Text>
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="medium">提现时效：</Text>
                    支持
                    <Text as="span" color={highlightColor} fontWeight="bold">T+1工作日</Text>
                    快速到账
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="medium">资金管理：</Text>
                    交易资金直接存入您的
                    <Text as="span" color={highlightColor} fontWeight="bold">微信/支付宝商户通账户</Text>
                    ，与日常经营账户打通，资金使用与提现更便捷
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="medium">抽佣方式：</Text>
                    采用
                    <Text as="span" color={redHighlight} fontWeight="bold">实时抽佣机制</Text>
                    ，交易完成即时扣除对应费用，账单清晰透明
                  </ListItem>
                </UnorderedList>
              </Box>
            </Box>

            <Divider />

            {/* 二、新增政策核心优势 */}
            <Box>
              <Heading as="h2" size="md" color={highlightColor} mb="4">
                二、新增政策核心优势
              </Heading>

              <OrderedList spacing="3" pl="4">
                <ListItem>
                  <Text as="span" fontWeight="bold" color={redHighlight}>成本大幅降低：</Text>
                  新增政策综合费率低至
                  <Text as="span" color={highlightColor} fontWeight="bold">0.6%</Text>
                  ，较原有模式（Moonbamboo抽
                  <Text as="span" color={secondaryHighlight}>0.6%</Text>
                  +微信/支付宝抽
                  <Text as="span" color={secondaryHighlight}>0.6%</Text>
                  ，总计1.2%）直接降低
                  <Text as="span" color={redHighlight} fontWeight="bold">50%</Text>
                  支付成本
                </ListItem>
                <ListItem>
                  <Text as="span" fontWeight="bold" color={redHighlight}>资金更灵活高效：</Text>
                  全新实时抽佣机制，无需提前向充值账户预存备用金，减少资金占用，经营周转更高效
                </ListItem>
                <ListItem>
                  <Text as="span" fontWeight="bold" color={redHighlight}>双模式自由切换：</Text>
                  在保留原有抽佣模式的基础上，新增两种差异化方案，您可根据资金管理习惯自由选择：
                  <UnorderedList mt="2" spacing="1">
                    <ListItem>偏好统一资金管理选「平台托管优惠方案」</ListItem>
                    <ListItem>习惯微信/支付宝账户体系选「商户通直通方案」</ListItem>
                  </UnorderedList>
                </ListItem>
              </OrderedList>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter 
          borderTop="1px" 
          borderColor={borderColor}
          justifyContent="center"
        >
          <Button 
            colorScheme="brand" 
            size="lg"
            px="12"
            onClick={handleClose}
            borderRadius="full"
            boxShadow="md"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'lg',
            }}
            transition="all 0.2s"
          >
            我已了解
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AnnouncementModal;
